import React, { PropsWithChildren, ReactNode } from 'react'

import { cn } from '@ps/ds/lib/utils'
import { Button as ShadButton, ButtonProps as ShadButtonProps } from '@ps/ds/ui/button'

import { IconButton, isIconButtonVariantType } from '../IconButton/IconButton'

export type ButtonProps = ShadButtonProps & {
  asChild?: boolean
  variant?: 'primary' | 'secondary' | 'muted' | 'ghost' | 'link'
  /**
   * When set, hides the button label below the given breakpoint, collapsing the button to icon-only on smaller screens.
   * Requires a `leadingIcon` or `trailingIcon` to be provided.
   * */
  collapseAt?: 'sm' | 'md'
  leadingIcon?: ReactNode
  loading?: boolean
  size?: 'sm' | 'md'
  trailingIcon?: ReactNode
}

const collapseClasses = {
  sm: { show: 'hidden sm:flex', hide: 'flex sm:hidden' },
  md: { show: 'hidden md:flex', hide: 'flex md:hidden' },
}

export const Button = React.forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  (
    {
      asChild = false,
      children,
      className,
      collapseAt,
      leadingIcon,
      loading = false,
      rounded,
      size = 'md',
      trailingIcon,
      variant = 'primary',
      ...props
    },
    ref,
  ) => {
    const shouldCollapse =
      !!collapseAt && isIconButtonVariantType(variant) && (!!leadingIcon || !!trailingIcon)

    return (
      <>
        <ShadButton
          data-type="button"
          ref={ref}
          size={size}
          variant={variant}
          loading={loading}
          leadingIcon={leadingIcon}
          trailingIcon={trailingIcon}
          asChild={asChild}
          rounded={rounded ?? size !== 'sm'}
          className={collapseAt ? cn(collapseClasses[collapseAt].show, className) : className}
          {...props}>
          {children}
        </ShadButton>

        {shouldCollapse && (
          <IconButton
            aria-label={props['aria-label']}
            asChild={asChild}
            className={collapseAt ? collapseClasses[collapseAt].hide : undefined}
            icon={leadingIcon || trailingIcon}
            loading={loading}
            ref={ref}
            size={size}
            variant={variant}
            {...props}
          />
        )}
      </>
    )
  },
)

Button.displayName = 'Button'
